import { useMemo } from 'react';
import { VITE_ONBOARDING_SCHEDULE_URL } from 'src/constants/envs';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { formatPhoneNumberWithCountryCode } from 'src/pages/Settings/components/SubscriptionsManager/components/Modals/CancelSubscription/SchedulingModal/SchedulingModal.functions';

/**
 * Returns the Calendly URL where a user can schedule a 1:1 session with a
 * LeadSales advisor. Pre-populates name / email / phone / workspace as query
 * params so the advisor sees the right context when the meeting is booked.
 *
 * Originally lived under Onboarding as `useOnboardingScheduleUrl`; promoted
 * here when the Dashboard Integrations flow started reusing the same advisor
 * help component.
 */
export function useAdvisorScheduleUrl(): string {
  const { currentUser } = useUserContext();
  const { currentWorkspace } = useWorkspaceContext();

  const scheduleUrl = useMemo(() => {
    if (!VITE_ONBOARDING_SCHEDULE_URL) return '';

    try {
      const url = new URL(VITE_ONBOARDING_SCHEDULE_URL);

      if (currentUser?.name) {
        url.searchParams.set('name', currentUser.name);
      }

      if (currentUser?.email) {
        url.searchParams.set('email', currentUser.email);
      }

      if (currentUser?.phoneNumber != null) {
        const formattedPhoneNumber = formatPhoneNumberWithCountryCode(
          String(currentUser.phoneNumber),
        );
        url.searchParams.set('a1', formattedPhoneNumber);
      }

      if (currentWorkspace?.id) {
        url.searchParams.set('a2', currentWorkspace.id);
      }

      return url.toString();
    } catch (error) {
      LLog.error(
        {
          component: LOG_COMPONENTS.UI,
          method: 'useAdvisorScheduleUrl',
          operation: LOG_OPERATIONS.UI_INTERACTION,
        },
        'Error parsing advisor schedule URL',
        error instanceof Error ? error : new Error(String(error)),
      );
      return VITE_ONBOARDING_SCHEDULE_URL;
    }
  }, [currentUser, currentWorkspace]);

  return scheduleUrl;
}
